import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, ArrowUp, Plus, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function Forum() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.forum));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/threads", { params: category ? { category } : {} }).then(({ data }) => {
      setThreads(data);
      setLoading(false);
    });
  }, [category]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 animate-fade-in" data-testid="forum-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent mb-2">Forum</div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">The Fish House</h1>
          <p className="text-muted-foreground mt-2">Reports, rigs, arguments and old lies from Florida sportsmen.</p>
        </div>
        <Button
          onClick={() => navigate(user ? "/threads/new" : "/login")}
          className="bg-primary hover:bg-primary/90"
          data-testid="btn-new-thread"
        >
          <Plus className="w-4 h-4 mr-2" /> New thread
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <aside className="md:col-span-4 lg:col-span-3 order-2 md:order-1">
          <div className="sticky top-24 space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Categories</div>
            <button
              onClick={() => setSearchParams({})}
              className={`w-full text-left px-4 py-3 rounded-md border transition-colors duration-200 ${
                !category ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
              }`}
              data-testid="cat-all"
            >
              <div className="font-semibold text-sm">All threads</div>
              <div className={`text-xs mt-0.5 ${!category ? "text-primary-foreground/80" : "text-muted-foreground"}`}>Everything, everywhere.</div>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setSearchParams({ category: c.id })}
                className={`w-full text-left px-4 py-3 rounded-md border transition-colors duration-200 ${
                  category === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
                }`}
                data-testid={`cat-${c.id}`}
              >
                <div className="font-semibold text-sm">{c.name}</div>
                <div className={`text-xs mt-0.5 ${category === c.id ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{c.description}</div>
              </button>
            ))}
          </div>
        </aside>

        <div className="md:col-span-8 lg:col-span-9 order-1 md:order-2 space-y-4" data-testid="thread-list">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading threads…
            </div>
          ) : threads.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="py-14 text-center">
                <div className="font-display font-bold text-xl">No threads here yet.</div>
                <p className="text-muted-foreground mt-2">Be the first to drop a report or start an argument.</p>
                <Button className="mt-6 bg-primary hover:bg-primary/90" onClick={() => navigate(user ? "/threads/new" : "/login")} data-testid="btn-empty-new-thread">
                  <Plus className="w-4 h-4 mr-2" /> Start a thread
                </Button>
              </CardContent>
            </Card>
          ) : (
            threads.map((t) => (
              <Link key={t.thread_id} to={`/threads/${t.thread_id}`} className="block group" data-testid={`thread-card-${t.thread_id}`}>
                <Card className="bg-card border-border hover:border-primary/40 transition-colors duration-200 shadow-[0_4px_24px_rgba(27,77,62,0.04)]">
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarImage src={t.author_picture} alt={t.author_name} />
                        <AvatarFallback>{t.author_name?.[0] || "?"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5">
                            {categories.find((c) => c.id === t.category)?.name || t.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {t.author_name} · {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <h3 className="font-display font-bold text-lg mt-2 group-hover:text-primary transition-colors">
                          {t.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.body}</p>
                        <div className="flex items-center gap-5 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><ArrowUp className="w-3.5 h-3.5" /> {t.upvotes}</span>
                          <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {t.reply_count}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
